// Dev-only preview switches for feature-flagged surfaces.
//
// Two-factor gated so this is dead code in production bundles:
//   1. process.env.NODE_ENV === "development" (CRA's `npm start`) OR
//      process.env.REACT_APP_ENV === "staging" (the staging build).
//      Production builds (REACT_APP_ENV === "production") are locked.
//   2. URL has ?devPreview=<name>             (off by default in dev too)
//
// Staging is allowed so internal QA can preview feature-flagged surfaces
// without going through the real role/onboarding flow. The actual prod-
// facing gates (e.g. `isPatient` from `usePeptidePricing`) derive from
// server-trusted session fields, so a URL flag in staging can never
// promote a real production user past those checks.
//
// Sticky for the tab: once you visit any page with ?devPreview=<name>, the
// flag is mirrored into sessionStorage so subsequent in-app navigations
// (which strip query params) keep preview mode on. Closing the tab clears
// it. Visit any URL with ?devPreview=off to clear it manually.
//
// Usage at consumer sites:
//   const previewOn = isDevPreview("peptide-patient");
//   const isPatient = pricing.isPatient || previewOn;
//
// Adding a new preview:
//   1. Pick a kebab-case name (e.g. "cp-promotion", "peptide-patient")
//   2. Add isDevPreview(<name>) at the gate site
//   3. Render <DevPreviewBadge /> near the top of the rendered JSX so the
//      tester can see they're in preview mode

const STORAGE_KEY = "__devPreview";
const OFF_TOKEN = "off";

function readSession() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSession(value) {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (value === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore — privacy mode, quota, etc. Preview just won't be sticky.
  }
}

// Exported so feature gates that want to auto-surface on local dev + staging
// (but never in production) can reuse this boundary instead of re-deriving it.
export function isPreviewAllowedEnv() {
  return process.env.NODE_ENV === "development" || process.env.REACT_APP_ENV === "staging";
}

function urlPreview() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("devPreview");
}

// Resolves the active preview by checking URL first, then sessionStorage.
// Side-effect: when the URL carries a devPreview param, the value is mirrored
// into sessionStorage so it survives in-app navigations that strip the query.
// `?devPreview=off` clears the session flag, opting out of sticky preview.
function resolveActive() {
  const urlVal = urlPreview();
  if (urlVal === OFF_TOKEN) {
    writeSession(null);
    return null;
  }
  if (urlVal) {
    writeSession(urlVal);
    return urlVal;
  }
  return readSession();
}

export function isDevPreview(name) {
  if (!isPreviewAllowedEnv()) return false;
  return resolveActive() === name;
}

// Returns the active preview name, or null. Used by DevPreviewBadge.
export function getActiveDevPreview() {
  if (!isPreviewAllowedEnv()) return null;
  return resolveActive();
}
